/**
 * Módulo Vuex para Administración de Agentes
 * Maneja state, actions y mutations para AgentsAdmin
 */

import { VuexModule, Module, Action, Mutation, getModule } from 'vuex-module-decorators';
import store from '@/store/store';
import { API } from '@/shared/api';
import { ssmHttpService } from '@/shared/services/http-service';
import {
    AgentOverviewDto, AgentsOverviewResponse, AgentStateDto, AgentDto, LicenseDto,
    ConnectionDto, HeartbeatDto, CommandDto, CommandResultDto, AuditEventDto,
    ReleaseDto, UpdateJobDto, SqlQueryRequestDto, SqlQueryResponseDto
} from '@/shared/dtos/agents-admin';

@Module({ dynamic: true, store, name: 'agents-admin-module' })
class AgentsAdminModule extends VuexModule {
    // ==================== STATE ====================
    public loading: boolean = false;
    public error: string = '';

    // Overview
    public overview: AgentsOverviewResponse = new AgentsOverviewResponse();

    // Agent State
    public agentState: AgentStateDto = new AgentStateDto();
    public agentFullState: any = null;

    // Lists
    public agents: AgentDto[] = [];
    public licenses: LicenseDto[] = [];
    public connections: ConnectionDto[] = [];
    public heartbeats: HeartbeatDto[] = [];
    public commands: CommandDto[] = [];
    public commandResults: CommandResultDto[] = [];
    public auditEvents: AuditEventDto[] = [];
    public releases: ReleaseDto[] = [];
    public updateJobs: UpdateJobDto[] = [];

    // SQL Query
    public sqlQueryResult: SqlQueryResponseDto = new SqlQueryResponseDto();
    public sqlQueryHistory: SqlQueryRequestDto[] = [];

    // Pagination
    public pageSize: number = 200;
    public totalRecords: number = 0;

    // ==================== MUTATIONS ====================
    @Mutation
    public setLoading(value: boolean) {
        this.loading = value;
    }

    @Mutation
    public setError(message: string) {
        this.error = message;
    }

    @Mutation
    public clearError() {
        this.error = '';
    }

    @Mutation
    public setOverview(data: AgentsOverviewResponse) {
        this.overview = data;
    }

    @Mutation
    public setAgentState(data: AgentStateDto) {
        this.agentState = data;
    }

    @Mutation
    public setAgentFullState(data: any) {
        this.agentFullState = data;
    }

    @Mutation
    public setAgents(data: any[]) {
        if (!Array.isArray(data)) {
            this.agents = [];
            return;
        }
        // Use AgentDto constructor for proper mapping
        this.agents = data.map(item => new AgentDto(item));
    }

    @Mutation
    public setLicenses(data: any[]) {
        if (!Array.isArray(data)) {
            this.licenses = [];
            return;
        }
        this.licenses = data.map(item => {
            const license = new LicenseDto();
            license.license_id = item.license_id || '';
            license.agent_id = item.agent_id || '';
            license.license_key = item.license_key || '';
            license.status = item.status || '';
            license.expiration_date = item.expiration_date ? new Date(item.expiration_date) : null;
            license.max_installations = item.max_installations || 0;
            license.current_installations = item.current_installations || 0;
            license.created_date = item.created_date ? new Date(item.created_date) : null;
            return license;
        });
    }

    @Mutation
    public setConnections(data: any[]) {
        if (!Array.isArray(data)) {
            this.connections = [];
            return;
        }
        this.connections = data.map(item => {
            const connection = new ConnectionDto();
            connection.connection_id = item.connection_id || '';
            connection.agent_id = item.agent_id || '';
            connection.installation_id = item.installation_id || '';
            connection.transport_type = item.transport_type || '';
            connection.connection_status = item.connection_status !== undefined ? item.connection_status : 0;
            connection.disconnect_reason = item.disconnect_reason || '';
            connection.is_current = item.is_current !== undefined ? item.is_current : false;
            connection.connected_at_utc = item.connected_at_utc ? new Date(item.connected_at_utc) : null;
            connection.last_seen_at_utc = item.last_seen_at_utc ? new Date(item.last_seen_at_utc) : null;
            connection.disconnected_at_utc = item.disconnected_at_utc ? new Date(item.disconnected_at_utc) : null;
            return connection;
        });
    }

    @Mutation
    public setHeartbeats(data: any[]) {
        if (!Array.isArray(data)) {
            this.heartbeats = [];
            return;
        }
        this.heartbeats = data.map(item => {
            const heartbeat = new HeartbeatDto();
            heartbeat.agent_id = item.agent_id || '';
            heartbeat.installation_id = item.installation_id || '';
            heartbeat.overall_health_status = item.overall_health_status !== undefined ? item.overall_health_status : 0;
            heartbeat.config_ok = item.config_ok !== undefined ? item.config_ok : false;
            heartbeat.sql_ok = item.sql_ok !== undefined ? item.sql_ok : false;
            heartbeat.sql_server = item.sql_server || '';
            heartbeat.sql_database = item.sql_database || '';
            heartbeat.sql_auth_mode = item.sql_auth_mode !== undefined ? item.sql_auth_mode : 0;
            heartbeat.sql_elapsed_milliseconds = item.sql_elapsed_milliseconds || 0;
            heartbeat.sql_server_version = item.sql_server_version || '';
            heartbeat.cpu_usage_percent = item.cpu_usage_percent || 0;
            heartbeat.memory_used_mb = item.memory_used_mb || 0;
            heartbeat.available_disk_mb = item.available_disk_mb || 0;
            heartbeat.last_error = item.last_error || '';
            heartbeat.last_error_at_utc = item.last_error_at_utc ? new Date(item.last_error_at_utc) : null;
            heartbeat.service_started_at_utc = item.service_started_at_utc ? new Date(item.service_started_at_utc) : null;
            heartbeat.snapshot_at_utc = item.snapshot_at_utc ? new Date(item.snapshot_at_utc) : null;
            heartbeat.received_at_utc = item.received_at_utc ? new Date(item.received_at_utc) : null;
            return heartbeat;
        });
    }

    @Mutation
    public setCommands(data: any[]) {
        if (!Array.isArray(data)) {
            this.commands = [];
            return;
        }
        this.commands = data.map(item => {
            const command = new CommandDto();
            command.command_id = item.command_id || item.id || '';
            command.agent_id = item.agent_id || '';
            command.installation_id = item.installation_id || '';
            command.correlation_id = item.correlation_id || '';
            command.command_name = item.command_name || '';
            command.command_type = item.command_type !== undefined ? item.command_type : 0;
            command.status = item.status !== undefined ? item.status : 0;
            command.timeout_seconds = item.timeout_seconds || 90;
            command.allow_retry = item.allow_retry !== undefined ? item.allow_retry : false;
            command.max_retries = item.max_retries !== undefined ? item.max_retries : 0;
            command.current_retry_count = item.current_retry_count !== undefined ? item.current_retry_count : 0;
            command.payload_json = item.payload_json || '';
            command.payload_type = item.payload_type || '';
            command.created_by_user_id = item.created_by_user_id || '';
            
            command.created_at = item.created_at_utc ? new Date(item.created_at_utc) : (item.created_at ? new Date(item.created_at) : null);
            command.sent_at = item.sent_at_utc ? new Date(item.sent_at_utc) : (item.sent_at ? new Date(item.sent_at) : null);
            command.accepted_at = item.accepted_at_utc ? new Date(item.accepted_at_utc) : (item.accepted_at ? new Date(item.accepted_at) : null);
            command.started_at = item.started_at_utc ? new Date(item.started_at_utc) : (item.started_at ? new Date(item.started_at) : null);
            command.completed_at = item.finished_at_utc ? new Date(item.finished_at_utc) : (item.completed_at ? new Date(item.completed_at) : null);
            return command;
        });
    }

    @Mutation
    public setCommandResults(data: any[]) {
        if (!Array.isArray(data)) {
            this.commandResults = [];
            return;
        }
        this.commandResults = data.map(item => {
            const result = new CommandResultDto();
            result.command_result_id = item.id || item.command_result_id || '';
            result.command_id = item.command_id || '';
            result.agent_id = item.agent_id || '';
            result.success = item.success !== undefined ? item.success : false;
            result.result_payload_type = item.result_payload_type || '';
            result.result_payload_json = item.result_payload_json || '';
            result.final_status = item.final_status !== undefined ? item.final_status : 0;
            result.duration_ms = item.duration_ms !== undefined ? item.duration_ms : 0;
            result.is_retryable = item.is_retryable !== undefined ? item.is_retryable : false;
            result.error_message = item.error_message || '';
            result.error_code = item.error_code || '';
            result.error_details = item.error_details || '';
            result.started_at = item.started_at_utc ? new Date(item.started_at_utc) : (item.started_at ? new Date(item.started_at) : null);
            result.finished_at = item.finished_at_utc ? new Date(item.finished_at_utc) : (item.finished_at ? new Date(item.finished_at) : null);
            result.created_at = item.created_at_utc ? new Date(item.created_at_utc) : (item.created_at ? new Date(item.created_at) : null);
            // Parse result_payload_json if available
            if (item.result_payload_json) {
                try {
                    result.result_data = typeof item.result_payload_json === 'string' ? JSON.parse(item.result_payload_json) : item.result_payload_json;
                } catch (e) {
                    result.result_data = item.result_payload_json;
                }
            }
            return result;
        });
    }

    @Mutation
    public setAuditEvents(data: any[]) {
        if (!Array.isArray(data)) {
            this.auditEvents = [];
            return;
        }
        this.auditEvents = data.map(item => {
            const event = new AuditEventDto();
            event.audit_id = item.id || item.event_id || item.audit_id || '';
            event.event_id = item.event_id || item.id || '';
            event.agent_id = item.agent_id || '';
            event.event_type = item.event_type || '';
            event.source = item.source || '';
            event.message = item.message || '';
            event.details_json = item.details_json || '';
            event.user_id = item.user_id || '';
            event.created_at = item.created_at_utc ? new Date(item.created_at_utc) : (item.created_at ? new Date(item.created_at) : null);
            return event;
        });
    }

    @Mutation
    public setReleases(data: any[]) {
        if (!Array.isArray(data)) {
            this.releases = [];
            return;
        }
        this.releases = data.map(item => {
            const release = new ReleaseDto();
            release.release_id = item.release_id || '';
            release.version = item.version || '';
            release.release_date = item.release_date ? new Date(item.release_date) : null;
            release.description = item.description || '';
            release.download_url = item.download_url || '';
            release.is_production = item.is_production !== undefined ? item.is_production : false;
            release.is_latest = item.is_latest !== undefined ? item.is_latest : false;
            release.checksum = item.checksum || '';
            return release;
        });
    }

    @Mutation
    public setUpdateJobs(data: any[]) {
        if (!Array.isArray(data)) {
            this.updateJobs = [];
            return;
        }
        this.updateJobs = data.map(item => {
            const job = new UpdateJobDto();
            job.update_job_id = item.update_job_id || '';
            job.agent_id = item.agent_id || '';
            job.from_version = item.from_version || '';
            job.to_version = item.to_version || '';
            job.status = item.status || '';
            job.progress_percent = item.progress_percent || 0;
            job.started_at = item.started_at ? new Date(item.started_at) : null;
            job.completed_at = item.completed_at ? new Date(item.completed_at) : null;
            return job;
        });
    }

    @Mutation
    public setSqlQueryResult(data: SqlQueryResponseDto) {
        this.sqlQueryResult = new SqlQueryResponseDto(data);
    }

    @Mutation
    public addSqlToHistory(query: SqlQueryRequestDto) {
        this.sqlQueryHistory.unshift(query);
        if (this.sqlQueryHistory.length > 20) {
            this.sqlQueryHistory.pop();
        }
    }

    @Mutation
    public setPageSize(value: number) {
        this.pageSize = value;
    }

    @Mutation
    public setTotalRecords(value: number) {
        this.totalRecords = value;
    }

    // ==================== ACTIONS ====================

    @Action({ rawError: true })
    public async getOverview(params: {
        tenantId?: string;
        agentId?: string;
        installationId?: string;
        take?: number;
    }) {
        try {
            this.setLoading(true);
            this.clearError();

            const response = await ssmHttpService.get(
                `${API.agentsAdmin}/overview`,
                {
                    tenantId: params.tenantId || '',
                    agentId: params.agentId || '',
                    installationId: params.installationId || '',
                    take: params.take || 100
                }
            );

            const overview = new AgentsOverviewResponse(response);
            this.setOverview(overview);

            // Hydrate tab datasets from overview so tabs render immediately on first load.
            if (Array.isArray(overview.agents)) this.setAgents(overview.agents as any[]);
            if (Array.isArray(overview.agent_connections)) this.setConnections(overview.agent_connections as any[]);
            if (Array.isArray(overview.agent_heartbeats)) this.setHeartbeats(overview.agent_heartbeats as any[]);
            if (Array.isArray(overview.agent_commands)) this.setCommands(overview.agent_commands as any[]);
            if (Array.isArray(overview.agent_command_results)) this.setCommandResults(overview.agent_command_results as any[]);
            if (Array.isArray(overview.agent_audit_events)) this.setAuditEvents(overview.agent_audit_events as any[]);
            if (Array.isArray(overview.agent_licenses)) this.setLicenses(overview.agent_licenses as any[]);
            if (Array.isArray(overview.agent_releases)) this.setReleases(overview.agent_releases as any[]);
            if (Array.isArray(overview.agent_update_jobs)) this.setUpdateJobs(overview.agent_update_jobs as any[]);

            return overview;
        } catch (error) {
            this.setError((error as any).message || 'Error al cargar overview');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    @Action({ rawError: true })
    public async getAgentState(params: {
        agentId: string;
        tenantId?: string;
        installationId?: string;
        take?: number;
    }) {
        try {
            this.setLoading(true);
            this.clearError();

            const response = await ssmHttpService.get(
                `${API.agentsAdmin}/agents/${params.agentId}/state`,
                {
                    tenantId: params.tenantId || '',
                    installationId: params.installationId || '',
                    take: params.take || 100
                }
            );

            this.setAgentFullState(response);
            const state = new AgentStateDto(response);
            this.setAgentState(state);
            return state;
        } catch (error) {
            this.setError((error as any).message || 'Error al cargar estado del agente');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    @Action({ rawError: true })
    public async getAgents(params: {
        tenantId?: string;
        agentId?: string;
        installationId?: string;
        take?: number;
    } = {}) {
        try {
            this.setLoading(true);
            this.clearError();

            const response = await ssmHttpService.get(
                `${API.agentsAdmin}/agents`,
                {
                    tenantId: params.tenantId || '',
                    agentId: params.agentId || '',
                    installationId: params.installationId || '',
                    take: params.take || 200
                }
            );

            // Handle multiple response formats
            let agentsArray: any[] = [];
            if (Array.isArray(response)) {
                agentsArray = response;
            } else if (response && Array.isArray(response.agents)) {
                agentsArray = response.agents;
            } else if (response && Array.isArray(response.data)) {
                agentsArray = response.data;
            }

            this.setAgents(agentsArray);
            this.setTotalRecords(agentsArray.length);
            return response;
        } catch (error) {
            this.setError((error as any).message || 'Error al cargar agentes');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    @Action({ rawError: true })
    public async getLicenses(params: {
        tenantId?: string;
        agentId?: string;
        installationId?: string;
        take?: number;
    } = {}) {
        try {
            this.setLoading(true);

            const response = await ssmHttpService.get(
                `${API.agentsAdmin}/licenses`,
                {
                    tenantId: params.tenantId || '',
                    agentId: params.agentId || '',
                    installationId: params.installationId || '',
                    take: params.take || 200
                }
            );

            this.setLicenses(response || []);
            return response;
        } catch (error) {
            this.setError((error as any).message || 'Error al cargar licencias');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    @Action({ rawError: true })
    public async getConnections(params: {
        tenantId?: string;
        agentId?: string;
        installationId?: string;
        take?: number;
    } = {}) {
        try {
            this.setLoading(true);

            const response = await ssmHttpService.get(
                `${API.agentsAdmin}/connections`,
                {
                    tenantId: params.tenantId || '',
                    agentId: params.agentId || '',
                    installationId: params.installationId || '',
                    take: params.take || 200
                }
            );

            this.setConnections(response || []);
            return response;
        } catch (error) {
            this.setError((error as any).message || 'Error al cargar conexiones');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    @Action({ rawError: true })
    public async getHeartbeats(params: {
        tenantId?: string;
        agentId?: string;
        installationId?: string;
        take?: number;
    } = {}) {
        try {
            this.setLoading(true);

            const response = await ssmHttpService.get(
                `${API.agentsAdmin}/heartbeats`,
                {
                    tenantId: params.tenantId || '',
                    agentId: params.agentId || '',
                    installationId: params.installationId || '',
                    take: params.take || 200
                }
            );

            this.setHeartbeats(response || []);
            return response;
        } catch (error) {
            this.setError((error as any).message || 'Error al cargar latidos');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    @Action({ rawError: true })
    public async getCommands(params: {
        tenantId?: string;
        agentId?: string;
        installationId?: string;
        take?: number;
    } = {}) {
        try {
            this.setLoading(true);

            const response = await ssmHttpService.get(
                `${API.agentsAdmin}/commands`,
                {
                    tenantId: params.tenantId || '',
                    agentId: params.agentId || '',
                    installationId: params.installationId || '',
                    take: params.take || 200
                }
            );

            this.setCommands(response || []);
            return response;
        } catch (error) {
            this.setError((error as any).message || 'Error al cargar comandos');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    @Action({ rawError: true })
    public async getCommandResults(params: {
        tenantId?: string;
        agentId?: string;
        installationId?: string;
        take?: number;
    } = {}) {
        try {
            this.setLoading(true);

            const response = await ssmHttpService.get(
                `${API.agentsAdmin}/command-results`,
                {
                    tenantId: params.tenantId || '',
                    agentId: params.agentId || '',
                    installationId: params.installationId || '',
                    take: params.take || 200
                }
            );

            this.setCommandResults(response || []);
            return response;
        } catch (error) {
            this.setError((error as any).message || 'Error al cargar resultados de comandos');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    @Action({ rawError: true })
    public async getAuditEvents(params: {
        tenantId?: string;
        agentId?: string;
        take?: number;
    } = {}) {
        try {
            this.setLoading(true);

            const response = await ssmHttpService.get(
                `${API.agentsAdmin}/audit-events`,
                {
                    tenantId: params.tenantId || '',
                    agentId: params.agentId || '',
                    take: params.take || 200
                }
            );

            this.setAuditEvents(response || []);
            return response;
        } catch (error) {
            this.setError((error as any).message || 'Error al cargar eventos de auditoría');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    @Action({ rawError: true })
    public async getReleases(params: { take?: number } = {}) {
        try {
            this.setLoading(true);

            const response = await ssmHttpService.get(
                `${API.agentsAdmin}/releases`,
                {
                    take: params.take || 200
                }
            );

            this.setReleases(response || []);
            return response;
        } catch (error) {
            this.setError((error as any).message || 'Error al cargar releases');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    @Action({ rawError: true })
    public async getUpdateJobs(params: {
        tenantId?: string;
        agentId?: string;
        take?: number;
    } = {}) {
        try {
            this.setLoading(true);

            const response = await ssmHttpService.get(
                `${API.agentsAdmin}/update-jobs`,
                {
                    tenantId: params.tenantId || '',
                    agentId: params.agentId || '',
                    take: params.take || 200
                }
            );

            this.setUpdateJobs(response || []);
            return response;
        } catch (error) {
            this.setError((error as any).message || 'Error al cargar trabajos de actualización');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    @Action({ rawError: true })
    public async executeSqlQuery(request: SqlQueryRequestDto) {
        try {
            this.setLoading(true);
            this.clearError();

            const response = await ssmHttpService.post(
                `${API.agentsAdmin}/sql/query`,
                request.toJson()
            );

            // El endpoint devuelve ACK con command_id; el resultado llega después en agent_command_results.
            const ack = new SqlQueryResponseDto(response);
            this.setSqlQueryResult(ack);
            this.addSqlToHistory(request);

            const commandId = response && response.command_id ? response.command_id : '';
            if (!commandId) {
                return ack;
            }

            const maxAttempts = 20;
            const delayMs = 1500;
            let finalResult: SqlQueryResponseDto | null = null;

            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                const pollResponse = await ssmHttpService.get(
                    `${API.agentsAdmin}/command-results`,
                    {
                        tenantId: request.tenant_id || '',
                        agentId: request.agent_id || '',
                        installationId: request.installation_id || '',
                        take: 500
                    },
                    false
                );

                const rows: any[] = Array.isArray(pollResponse) ? pollResponse : [];
                this.setCommandResults(rows);

                const match = rows.find((x: any) => x && x.command_id === commandId);
                if (match) {
                    let parsedPayload: any = null;
                    if (match.result_payload_json) {
                        try {
                            parsedPayload = typeof match.result_payload_json === 'string'
                                ? JSON.parse(match.result_payload_json)
                                : match.result_payload_json;
                        } catch (e) {
                            parsedPayload = null;
                        }
                    }

                    let resultRows: any[] = [];
                    if (Array.isArray(parsedPayload)) {
                        resultRows = parsedPayload;
                    } else if (parsedPayload && Array.isArray(parsedPayload.rows)) {
                        resultRows = parsedPayload.rows;
                    }

                    const columns = resultRows.length > 0 ? Object.keys(resultRows[0]) : [];
                    const message = match.success
                        ? 'Resultado recibido.'
                        : (match.error_message || match.error_details || 'La ejecución del comando falló.');

                    finalResult = new SqlQueryResponseDto({
                        success: match.success === true,
                        command_id: match.command_id || '',
                        message,
                        rows: resultRows,
                        columns,
                        rows_affected: resultRows.length,
                        execution_time_ms: match.duration_ms || 0
                    });
                    break;
                }

                await new Promise(resolve => setTimeout(resolve, delayMs));
            }

            if (!finalResult) {
                finalResult = new SqlQueryResponseDto({
                    success: false,
                    command_id: commandId,
                    message: 'No se recibió resultado del comando en el tiempo esperado.',
                    rows: [],
                    columns: [],
                    rows_affected: 0,
                    execution_time_ms: 0
                });
            }

            this.setSqlQueryResult(finalResult);
            return finalResult;
        } catch (error) {
            this.setError((error as any).message || 'Error al ejecutar consulta SQL');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }


    @Action({ rawError: true })
    public async executeAgendaServicesQuery(params: {
        agentId: string;
        installationId?: string;
        tenantId?: string;
    }) {
        try {
            this.setLoading(true);
            this.clearError();

            const response = await ssmHttpService.get(
                `${API.agentsAdmin}/sql/agenda-services/${params.agentId}`,
                {
                    installationId: params.installationId || '',
                    tenantId: params.tenantId || ''
                }
            );

            return response;
        } catch (error) {
            this.setError((error as any).message || 'Error al ejecutar consulta de servicios de agenda');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }
}

export const agentsAdminModule = getModule(AgentsAdminModule);
